import type { ShelvingCellConfig, WardrobeModule } from "../types";
import { defaultShelvingCell, getShelvingGridLayout } from "./shelving";

export type ShelvingDesignKind = "tall" | "low" | "special";

interface ShelvingDesignDefinition {
  name: string;
  description: string;
  kind: ShelvingDesignKind;
  width: number;
  height: number;
  depth: number;
  columns: readonly number[];
  rows: readonly number[];
  fullBack?: boolean;
  /**
   * Отделка, снятая с фотографии схемы в витрине: материал корпуса и, если
   * цвет не совпадает ни с одним материалом, однотонная покраска поверх него.
   * Без этих полей изделие приходит белым.
   */
  material?: "warmOak" | "linen" | "matteWhite" | "walnut" | "graphite";
  color?: string;
  /** Телевизор в витрине: на столешнице тумбы или на стене над буфетом. */
  tv?: "stand" | "wall";
}

/**
 * Editable starting points based on common configurable-furniture typologies:
 * regular grid, gradient, rhythmic pattern, framed display and low sideboards.
 * They intentionally use our own dimensions and construction rules.
 */
export const SHELVING_DESIGNS = {
  grid: {
    name: "Книжный · Сетка",
    description: "Спокойная равномерная сетка для книг и коллекций",
    kind: "tall",
    width: 1600,
    height: 2200,
    depth: 350,
    columns: [1, 1, 1, 1],
    rows: [1, 1, 1, 1, 1],
    material: "matteWhite",
    color: "#BCD0CC"
  },
  gradient: {
    name: "Градиент",
    description: "Проёмы плавно меняют ширину и высоту",
    kind: "tall",
    width: 1800,
    height: 2200,
    depth: 350,
    columns: [0.78, 1, 1.38, 0.92],
    rows: [0.72, 0.9, 1.18, 1.38, 0.95],
    material: "matteWhite",
    color: "#CFCDCC"
  },
  pattern: {
    name: "Ритм",
    description: "Смещённые стойки создают живой геометрический рисунок",
    kind: "tall",
    width: 1500,
    height: 2200,
    depth: 350,
    columns: [1, 1.18, 1],
    rows: [1, 0.82, 1.22, 0.9, 1.14, 0.96],
    material: "linen"
  },
  frame: {
    name: "Витрина · Рамка",
    description: "Закрытая рамка вокруг открытой выставочной части",
    kind: "tall",
    width: 1400,
    height: 1800,
    depth: 400,
    columns: [0.9, 1.18, 1.18, 0.9],
    rows: [1, 1.12, 1.12, 1],
    fullBack: true,
    material: "matteWhite"
  },
  tall: {
    name: "Узкий высокий",
    description: "Компактная библиотека для простенка или ниши",
    kind: "tall",
    width: 650,
    height: 2200,
    depth: 350,
    columns: [1, 1.15],
    rows: [1.1, 1, 1.5, 0.65, 1.1, 1]
  },
  cabinet: {
    name: "Закрытый низ",
    description: "Большая библиотека с закрытым хранением внизу",
    kind: "tall",
    width: 1800,
    height: 2200,
    depth: 400,
    columns: [1, 1.05, 1.1, 0.9],
    rows: [1.55, 0.9, 1, 1, 1.1],
    fullBack: true,
    material: "warmOak"
  },
  low: {
    name: "Низкий буфет",
    description: "Закрытое хранение снизу и открытая полка для декора",
    kind: "low",
    width: 1600,
    height: 900,
    depth: 400,
    columns: [1, 1, 1, 1],
    rows: [1.35, 0.8],
    material: "matteWhite",
    tv: "wall"
  },
  media: {
    name: "Медиа-тумба",
    description: "Длинная композиция с открытой центральной нишей",
    kind: "low",
    width: 2000,
    height: 1000,
    depth: 400,
    columns: [0.9, 1, 1.25, 1, 0.9],
    rows: [1.18, 0.82],
    fullBack: true,
    material: "matteWhite",
    tv: "stand"
  },
  wallStorage: {
    name: "Во всю стену",
    description: "Широкое хранение с открытым верхом и закрытым нижним поясом",
    kind: "tall",
    width: 2400,
    height: 2200,
    depth: 400,
    columns: [0.9, 1, 1.12, 1.12, 1, 0.9],
    rows: [1.28, 1, 0.92, 1.12, 0.82],
    fullBack: true,
    material: "matteWhite"
  },
  mosaic: {
    name: "Книжный · Мозаика",
    description: "Асимметричная книжная композиция из ячеек разной ширины",
    kind: "tall",
    width: 1700,
    height: 2200,
    depth: 350,
    columns: [0.86, 1.18, 0.96, 1.1],
    rows: [1.12, 0.82, 1.24, 0.9, 1.08],
    material: "matteWhite",
    color: "#BCD0CC"
  },
  highSideboard: {
    name: "Высокий буфет",
    description: "Буфет с витринным верхом, дверями и ящиками",
    kind: "low",
    width: 1800,
    height: 1250,
    depth: 400,
    columns: [1, 1.08, 1.08, 1],
    rows: [1.15, 0.9, 0.82],
    fullBack: true,
    material: "matteWhite",
    color: "#B15D39"
  },
  vinylStorage: {
    name: "Хранение винила",
    description: "Глубокие квадратные ячейки для пластинок и аппаратуры",
    kind: "special",
    width: 1200,
    height: 1500,
    depth: 400,
    columns: [1, 1, 1],
    rows: [1, 1, 0.82, 0.82],
    fullBack: true
  },
  chest: {
    name: "Комод · 5 ящиков",
    description: "Низкий комод с двумя секциями и пятью ящиками",
    kind: "special",
    width: 1770,
    // 900 мм: в левую секцию помещаются три ящика с фасадом от 250 мм.
    // При 830 влезало только два, и комод «5 ящиков» собирался с четырьмя (приёмка 04.10, cat-07).
    height: 900,
    depth: 400,
    columns: [1.04, 0.96],
    rows: [1],
    fullBack: true,
    material: "graphite",
    color: "#595756"
  }
} as const satisfies Record<string, ShelvingDesignDefinition>;

export type ShelvingDesign = keyof typeof SHELVING_DESIGNS;

const setDoor = (cell: ShelvingCellConfig, column: number) => {
  cell.content = "door";
  cell.handle = "push";
  cell.doorHinge = column % 2 === 0 ? "left" : "right";
};

const setDrawers = (cell: ShelvingCellConfig) => {
  cell.content = "drawers";
  cell.drawerCount = 2;
  cell.handle = "push";
};

const configureDesignCell = (
  cell: ShelvingCellConfig,
  design: ShelvingDesign,
  column: number,
  row: number,
  columns: number,
  rows: number
) => {
  if (design === "grid") {
    cell.backPanel = (column + row * 2) % 5 === 0;
    return;
  }

  if (design === "gradient") {
    cell.backPanel = (column * 2 + row) % 5 === 0;
    if (column === 2 && row % 2 === 0) {
      cell.extraDivider = true;
      cell.extraDividerMode = "offset";
      cell.extraDividerPosition = row % 4 === 0 ? 0.34 : 0.66;
    }
    return;
  }

  if (design === "pattern") {
    cell.backPanel = (column + row) % 4 === 0;
    if (column === 1 || (column === 0 && row % 3 === 1)) {
      cell.extraDivider = true;
      cell.extraDividerMode = "offset";
      cell.extraDividerPosition = row % 2 === 0 ? 0.32 : 0.68;
    }
    return;
  }

  if (design === "frame") {
    const onOuterFrame = column === 0 || column === columns - 1;
    if (row === 0 && onOuterFrame) setDoor(cell, column);
    if (row === 0 && !onOuterFrame) setDrawers(cell);
    return;
  }

  if (design === "tall") {
    cell.backPanel = column === columns - 1 || row === rows - 1;
    if (column === 0 && row % 2 === 1) {
      cell.extraDivider = true;
      cell.extraDividerMode = "offset";
      cell.extraDividerPosition = row % 4 === 1 ? 0.36 : 0.64;
    }
    return;
  }

  if (design === "cabinet") {
    if (row === 0) setDoor(cell, column);
    return;
  }

  if (design === "low") {
    if (row === 0) setDoor(cell, column);
    else cell.backPanel = column % 2 === 1;
    return;
  }

  if (design === "media") {
    if (row === 0 && (column === 0 || column === columns - 1)) {
      setDoor(cell, column);
    } else if (row === 0 && (column === 1 || column === columns - 2)) {
      setDrawers(cell);
    }
    return;
  }

  if (design === "wallStorage") {
    if (row === 0) {
      if (column === 2 || column === 3) setDrawers(cell);
      else setDoor(cell, column);
    } else if (row === 1 && (column === 0 || column === columns - 1)) {
      setDoor(cell, column);
    } else {
      cell.backPanel = (column * 2 + row) % 4 === 0;
    }
    return;
  }

  if (design === "mosaic") {
    cell.backPanel = (column + row * 2) % 4 === 1;
    if (row === 0 && (column === 0 || column === columns - 1)) {
      setDoor(cell, column);
    } else if (
      (column === 1 && row === 1) ||
      (column === 2 && row === 3) ||
      (column === 0 && row === rows - 1)
    ) {
      cell.extraDivider = true;
      cell.extraDividerMode = "offset";
      cell.extraDividerPosition = (column + row) % 2 === 0 ? 0.34 : 0.66;
    }
    return;
  }

  if (design === "highSideboard") {
    if (row === 0) {
      if (column === 1 || column === 2) setDrawers(cell);
      else setDoor(cell, column);
    } else if (row === 1 && (column === 0 || column === columns - 1)) {
      setDoor(cell, column);
    }
    return;
  }

  if (design === "vinylStorage") {
    cell.backPanel = true;
    if (row === rows - 1 && (column === 0 || column === columns - 1)) {
      setDoor(cell, column);
    } else if (row === rows - 1 && column === 1) {
      setDrawers(cell);
    }
    return;
  }

  if (design === "chest") {
    setDrawers(cell);
    cell.drawerCount = column === 0 ? 3 : 2;
  }
};

/** Templates describe editable construction, not decorative overlays. */
export function shelvingDesignPatch(
  module: WardrobeModule,
  design: ShelvingDesign,
  ceilingHeight: number
): Partial<WardrobeModule> {
  // Аннотация нужна, чтобы у схем без своей отделки необязательные поля тоже были видны.
  const definition: ShelvingDesignDefinition = SHELVING_DESIGNS[design];
  const next: WardrobeModule = {
    ...module,
    width: definition.width,
    height: Math.min(definition.height, ceilingHeight),
    depth: definition.depth,
    shelvingBase: module.shelvingBase ?? "plinth",
    shelvingBackPanel:
      "fullBack" in definition ? definition.fullBack : false,
    shelvingColumns: definition.columns.length,
    shelvingRows: definition.rows.length,
    shelvingColumnRatios: [...definition.columns],
    shelvingRowRatios: [...definition.rows],
    shelvingCells: []
  };
  const grid = getShelvingGridLayout(next);
  for (let row = 0; row < grid.rows; row += 1) {
    for (let column = 0; column < grid.columns; column += 1) {
      const cell = defaultShelvingCell(module.id, column, row);
      configureDesignCell(
        cell,
        design,
        column,
        row,
        grid.columns,
        grid.rows
      );
      next.shelvingCells!.push(cell);
    }
  }
  return {
    shelvingDesign: design,
    // Белый остаётся значением по умолчанию: он подставляется там, где у схемы нет своей отделки.
    material: definition.material ?? "matteWhite",
    facadeMaterial: definition.material ?? "matteWhite",
    shelvingColorHex: definition.color,
    shelvingTv: definition.tv,
    width: next.width,
    height: next.height,
    depth: next.depth,
    shelvingColumns: grid.columns,
    shelvingRows: grid.rows,
    shelvingColumnRatios: next.shelvingColumnRatios,
    shelvingRowColumnRatios: undefined,
    shelvingRowRatios: next.shelvingRowRatios,
    shelvingBase: next.shelvingBase,
    shelvingBackPanel: next.shelvingBackPanel,
    shelvingCells: next.shelvingCells
  };
}

/**
 * Saved projects may carry a scheme id that no longer exists in the catalogue,
 * so every lookup goes through this guard instead of indexing blindly.
 */
export function isShelvingDesign(value: unknown): value is ShelvingDesign {
  return (
    typeof value === "string" &&
    Object.prototype.hasOwnProperty.call(SHELVING_DESIGNS, value)
  );
}

/** Name shown on the "started from this scheme" badge, or null for an unknown id. */
export function shelvingDesignName(value: unknown): string | null {
  return isShelvingDesign(value) ? SHELVING_DESIGNS[value].name : null;
}
